import { useEffect } from 'react';
import Hls from 'hls.js';

/**
 * Attach an HLS or MP4 source to a `<video>` element with sensible fallbacks.
 *
 *   - If the URL is an `.m3u8` and the browser is Safari/iOS, set `src` directly
 *     (native HLS is built in there — no JS shim needed, lower CPU usage).
 *   - If the URL is `.m3u8` and the browser is Chromium/Firefox, attach hls.js
 *     and let it MSE-stream the segments.
 *   - Otherwise (MP4 fallback URL, legacy `file_url`, etc.) just set `src`.
 *
 * Cleans up the hls.js instance on unmount or src change to avoid leaks.
 *
 * @param videoRef React ref to a <video> element
 * @param src      Source URL — either an .m3u8 playlist or a direct file URL
 * @param opts     { autoStart: boolean }  — start HLS immediately on attach
 */
export function useHlsVideo(videoRef, src, opts = {}) {
  const { autoStart = true } = opts;

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return undefined;

    const isHls = typeof src === 'string' && src.toLowerCase().includes('.m3u8');

    // Safari & iOS: native HLS via plain src. Avoid loading hls.js entirely here —
    // it pegs CPU on iOS, and Safari decodes HLS in hardware.
    const supportsNative = video.canPlayType('application/vnd.apple.mpegurl') !== '';
    if (isHls && supportsNative) {
      video.src = src;
      return () => {
        try { video.removeAttribute('src'); video.load(); } catch {
          /* DOM may already be torn down */
        }
      };
    }

    // hls.js path for everything else
    if (isHls && Hls.isSupported()) {
      const hls = new Hls({
        // Tighter buffer for the swipe feed so we don't waste data on videos
        // the user is about to swipe past.
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        startLevel: -1, // ABR auto
      });
      hls.loadSource(src);
      hls.attachMedia(video);
      if (autoStart) {
        // No-op if media isn't ready yet; hls.js handles the deferred start.
        hls.once(Hls.Events.MANIFEST_PARSED, () => {
          video.play().catch(() => { /* autoplay blocked is fine */ });
        });
      }
      return () => {
        try { hls.destroy(); } catch { /* already destroyed */ }
      };
    }

    // Direct (non-HLS) URL — legacy file_url, MP4 fallback, blob: from upload preview, etc.
    video.src = src;
    if (autoStart) {
      // iOS Safari ignores the `autoPlay` attribute when the `src` is attached
      // dynamically via JS AFTER the initial render. Without this explicit
      // play() call the video sits at 0:00 even though the UI's isPlaying
      // state has flipped to true — which is exactly what users see for
      // legacy pre-Bunny MP4s on iPhone (e.g. dyter07's "Sumerian Music" 
      // showing a black screen with frozen 0:00).
      const startMp4 = () => {
        video.play().catch(() => { /* user-gesture required or already playing — both safe to ignore */ });
      };
      if (video.readyState >= 2 /* HAVE_CURRENT_DATA */) {
        startMp4();
      } else {
        video.addEventListener('loadedmetadata', startMp4, { once: true });
      }
    }
    return () => {
      try { video.removeAttribute('src'); video.load(); } catch {
        /* noop */
      }
    };
  }, [videoRef, src, autoStart]);
}

/**
 * Resolve the best playback URL for a video document.
 * Prefers HLS (Bunny) when the video has finished transcoding; falls back to
 * the legacy /api/files URL for older catalogue entries.
 */
export function resolvePlaybackUrl(video) {
  if (!video) return '';
  // New (Bunny) path: HLS playlist takes precedence once status flips to approved/ready.
  if (video.hls_url && (video.status === 'approved' || video.status === 'ready' || video.status === 'pending_review')) {
    return video.hls_url;
  }
  // Legacy path: served via FastAPI Range endpoint from object storage.
  if (video.video_url) return video.video_url;
  if (video.file_url) return video.file_url;
  if (video.video_path) {
    const api = process.env.REACT_APP_BACKEND_URL || '';
    return `${api}/api/files/${video.video_path}`.replace(/\/+$/g, '');
  }
  return '';
}

/**
 * Whether a video is currently transcoding (Bunny pipeline). UI should show a
 * "Processing…" placeholder instead of attempting to play.
 */
export function isVideoProcessing(video) {
  if (!video) return false;
  if (video.status === 'processing') return true;
  // Defensive: if Bunny is in use but transcoding hasn't completed yet, hls_url
  // will be missing even when status hasn't been explicitly set to processing.
  if (video.bunny_video_guid && !video.hls_url && video.status !== 'failed') return true;
  return false;
}

/**
 * Whether a video has a stored playable asset (legacy file OR Bunny upload).
 *
 * Returns true the MOMENT a Bunny upload succeeds (`bunny_video_guid` is set),
 * which happens before the webhook flips status → "ready". This is what stops
 * the admin grid showing "No file" on freshly-uploaded HLS videos while Bunny
 * is still transcoding. (BUG 2.)
 *
 * Callers that need to differentiate "uploaded but still transcoding" vs
 * "playable now" should ALSO check `isVideoProcessing(video)` or `video.status`
 * — this helper intentionally doesn't read the webhook-driven status field.
 */
export function hasVideoFile(video) {
  if (!video) return false;
  if (video.bunny_video_guid) return true;
  if (video.hls_url) return true;
  if (video.video_path) return true;
  if (video.file_path) return true;
  if (video.video_url) return true;
  if (video.file_url) return true;
  return false;
}

/**
 * Branded AUDIVUE placeholder used when a video has NO thumbnail at all.
 * Inline SVG → no extra HTTP request, no asset to ship, no broken-image flash.
 * Wordmark is intentionally ALL-CAPS "AUDIVUE" per brand spec.
 */
export const AUDIVUE_PLACEHOLDER =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 450">' +
      '<defs>' +
        '<linearGradient id="g" x1="0" y1="0" x2="1" y2="1">' +
          '<stop offset="0%" stop-color="#0a0a0a"/>' +
          '<stop offset="100%" stop-color="#18181b"/>' +
        '</linearGradient>' +
      '</defs>' +
      '<rect width="800" height="450" fill="url(#g)"/>' +
      '<g fill="none" stroke="#27272a" stroke-width="1">' +
        '<circle cx="400" cy="225" r="80"/>' +
        '<circle cx="400" cy="225" r="130"/>' +
        '<circle cx="400" cy="225" r="180"/>' +
      '</g>' +
      '<text x="400" y="240" text-anchor="middle" font-family="system-ui,Inter,sans-serif" ' +
        'font-size="56" font-weight="800" letter-spacing="8" fill="#fafafa">AUDIVUE</text>' +
    '</svg>'
  );

/**
 * Resolve the best thumbnail URL for a video. Single source of truth used by
 * EVERY card surface (Home, Discover, My Library, profiles, admin grid) so the
 * priority is consistent across the app.
 *
 * Priority:
 *   1. custom_thumbnail_url   — creator's hand-picked image (highest)
 *   2. thumbnail_url          — Bunny's real `thumbnail.jpg` (auto-generated)
 *   3. legacy `/api/files/{thumbnail_path}` — older catalogue entries
 *   4. local thumbnail field  — old upload paths
 *   5. AUDIVUE branded placeholder (default fallback) — caller can override
 *      with a custom fallback (e.g. Pulse uses GENRE_THUMBS to keep the
 *      vertical feed looking lively while the hook video loads).
 *
 * Blurhash is NEVER returned as the final thumbnail. If a video doc has a
 * blurhash field, it should be used only as a `background-image` while the
 * real image loads — not as the displayed thumbnail.
 */
export function resolveThumbnailUrl(video, fallback = AUDIVUE_PLACEHOLDER) {
  if (!video) return fallback;
  if (video.custom_thumbnail_url) return video.custom_thumbnail_url;
  if (video.thumbnail_url) return video.thumbnail_url;
  const api = process.env.REACT_APP_BACKEND_URL || '';
  if (video.thumbnail_path) return `${api}/api/files/${video.thumbnail_path}`.replace(/\/+$/g, '');
  if (video.thumbnail) {
    const t = video.thumbnail;
    if (t.startsWith('http')) return t;
    return `${api}${t.startsWith('/') ? '' : '/'}${t}`;
  }
  return fallback;
}
